<script setup>
import { computed, ref } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import AiAgentBadge from 'dashboard/components/widgets/conversation/AiAgentBadge.vue';
import HeaderAssigneePill from 'dashboard/components/widgets/conversation/header/HeaderAssigneePill.vue';
import HeaderFunnelPill from 'dashboard/components/widgets/conversation/header/HeaderFunnelPill.vue';
import TasksChip from 'dashboard/components-next/Conversation/ContextChips/TasksChip.vue';
import AgendaChip from 'dashboard/components-next/Conversation/ContextChips/AgendaChip.vue';
import ScheduleMessageModal from 'dashboard/components/widgets/conversation/bubble/ScheduleMessageModal.vue';
import { useHeaderPins } from 'dashboard/composables/useHeaderPins';

/**
 * Módulos da conversa no trilho da direita (V3+V2, mockup final-v3v2.html).
 *
 * Não reimplementa nada: são os mesmos cinco componentes que viviam na linha do
 * cabeçalho, agora empilhados no trilho e em modo ícone. O que muda é onde
 * moram, não o que fazem, então toda a lógica de trocar responsável, mover
 * etapa e agendar continua a mesma, já testada.
 *
 * Ordem imutável (o mockup trava): IA · Responsável · Funil · Tarefas · Agenda.
 * Um popover por vez, como no cabeçalho.
 */
const store = useStore();
// O trilho mostra so o que mora na barra: fixado sobe pro topo, e o que a
// pessoa mandou pro menu some daqui. Um modulo nunca aparece em dois lugares.
const { naBarra } = useHeaderPins();
const naBarraTem = modulo => naBarra.value.includes(modulo);

const chat = useMapGetter('getSelectedChat');
const abertoNo = ref(null);

const conversa = computed(() => chat.value || {});
const temConversa = computed(() => Boolean(conversa.value?.id));
const inbox = computed(() =>
  store.getters['inboxes/getInbox'](conversa.value?.inbox_id)
);
const responsavel = computed(() => conversa.value?.meta?.assignee);
const agendarAberto = ref(false);

const alternar = chave => {
  abertoNo.value = abertoNo.value === chave ? null : chave;
};
const fechar = () => {
  abertoNo.value = null;
};
const aoAgendarMensagem = () => {
  fechar();
  agendarAberto.value = true;
};
</script>

<template>
  <div
    v-if="temConversa"
    class="flex shrink-0 flex-col items-center gap-1.5 px-1"
    data-testid="rail-modules"
  >
    <span v-if="naBarraTem('ai')" class="contents" data-testid="rail-module-ai">
      <AiAgentBadge
        rail
        pinnable
        compact
        :open="abertoNo === 'ai'"
        :inbox-name="inbox?.name || ''"
        @toggle="alternar('ai')"
        @close="fechar"
      />
    </span>
    <span
      v-if="naBarraTem('agent')"
      class="contents"
      data-testid="rail-module-assignee"
    >
      <HeaderAssigneePill
        rail
        pinnable
        :conversation-id="conversa.id"
        :inbox-id="conversa.inbox_id"
        :assignee="responsavel"
        compact
        :open="abertoNo === 'agent'"
        @toggle="alternar('agent')"
        @close="fechar"
      />
    </span>
    <span
      v-if="naBarraTem('funnel')"
      class="contents"
      data-testid="rail-module-funnel"
    >
      <HeaderFunnelPill
        rail
        pinnable
        :conversation="conversa"
        :open="abertoNo === 'funnel'"
        @toggle="alternar('funnel')"
        @close="fechar"
      />
    </span>
    <span
      v-if="naBarraTem('tasks')"
      class="contents"
      data-testid="rail-module-tasks"
    >
      <TasksChip
        :conversation="conversa"
        pinnable
        :open="abertoNo === 'tasks'"
        compact
        variant="icon"
        @toggle="alternar('tasks')"
        @close="fechar"
      />
    </span>
    <span
      v-if="naBarraTem('agenda')"
      class="contents"
      data-testid="rail-module-agenda"
    >
      <AgendaChip
        :conversation="conversa"
        pinnable
        :open="abertoNo === 'agenda'"
        compact
        variant="icon"
        @toggle="alternar('agenda')"
        @close="fechar"
        @schedule-message="aoAgendarMensagem"
      />
    </span>
    <ScheduleMessageModal
      v-if="agendarAberto && conversa.id"
      :show="agendarAberto"
      :conversation-id="conversa.display_id ?? conversa.id"
      @close="agendarAberto = false"
    />
  </div>
</template>
